import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  checkDuplicatePhoneCount,
  fetchGirabatcomOrders,
  updateOrderStatus,
  deleteOrder,
  fetchGirabatcomOrdersSummary,
  markOrdersSentToFincartBulk,
  sendSentToCartWhatsApp,
} from "../../api/OrderAPIs";
import {
  fetchFincartGovernorates,
  getDefaultPickupLocationId,
  sendOrderToFincart,
  saveFincartOrderId,
  getStoredFincartOrderId,
  fetchFincartThermalLabelsPdf,
  blobToBase64,
} from "../../api/FincartAPIs";
import { printPdfBase64, printOrderPieceLabels } from "../../utils/qzTray";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { formatPhoneWithCaseStyle, teePlusPackedItemData, caseStyleNameFromDetail, detailHasPersonalizedName } from "../../utils/orderLineDisplay";
import { CaseStyleBadge, PersonalizedBadge } from "../../Components/orders/CaseStyleBadge";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import { EGYPTIAN_GOVERNORATES } from "../../constants/governorates";
import { ShippingTrackButton } from "../../Components/orders/ShippingTimeline";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  AlertTriangle,
  ShoppingCart,
  Truck,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Minus,
  Search,
  Loader,
  TrendingUp,
  Send,
  CheckSquare,
  Printer,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const GirabatcomOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isSendingFincart, setIsSendingFincart] = useState(false);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const [isPrintingOrderPieces, setIsPrintingOrderPieces] = useState(false);
  const [isMarkingAsOld, setIsMarkingAsOld] = useState(false);
  const [isMarkingAsPrinted, setIsMarkingAsPrinted] = useState(false);
  /** all | sent | notSent | dup */
  const [fincartFilter, setFincartFilter] = useState("all");
  const [governorateFilter, setGovernorateFilter] = useState("all");

  const { data: girabatcomOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["girabatcomOrdersSummary"],
    queryFn: fetchGirabatcomOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (!successful) {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            caseStyleName: caseStyleNameFromDetail(detail),
            hasPersonalizedName: detailHasPersonalizedName(detail),
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          city: order.address?.city || "",
          apartment: order.address?.apartment || "",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "Girabatcom",
          sentToFincart: !!order.sentToFincart || !!order.shippingOrderId || !!getStoredFincartOrderId(order.orderId),
          fincartOrderId: order.shippingOrderId || getStoredFincartOrderId(order.orderId),
          shippingState: order.shippingState || null,
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchGirabatcomOrders(0, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchGirabatcomOrders(currentPage, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchGirabatcomOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setHasMore(data.length === newSize);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Incomplete") {
      const note = window.prompt(
        "ملاحظة Incomplete (اختياري) — السبب أو نوع الجراب الناقص:\nاتركه فاضي لو مش عايز تكتب حاجة"
      );
      if (note === null) return;
      reason = note.trim() || "no reason (normal order)";
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${currentReference}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`${currentReference}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join("\n");
      await copyOrderToTeePlus(finalText, order.orderNumber);

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const isOrderNonCustom = (order) => {
    return !order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));
  };

  const handleCopyAllVisibleOrders = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }

    const confirm = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirm) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );

        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }

        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

        const items = order.items;
        const orderLines = [];

        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const itemData = teePlusPackedItemData(item);
          const itemLine = `${itemNum}${itemData}`;

          if (index === 0) {
            orderLines.push(`${customerBase}\t\t${itemLine}`);
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });

        finalLines.push(...orderLines);

        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const finalText = finalLines.join("\n");
    try {
      await copyOrderToTeePlus(finalText, nonCustomOrders.map((o) => o.orderNumber));
      toast.success(
        `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
        { autoClose: 6000 }
      );
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleMarkSelectedAsOld = async () => {
    // Use loadedOrders (not filteredOrders) so search filter can't hide selected IDs.
    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "قديم"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingAsOld(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of selected) {
      try {
        // Reason is required by API model binding (same as single "قديم" button).
        await updateOrderStatus(
          order.orderNumber,
          "Old",
          "تم التحويل للقديم من Girabatcom"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-old");
    setIsMarkingAsOld(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى قديم` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى قديم بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleMarkSelectedAsPrinted = async () => {
    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "Printed"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingAsPrinted(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى Printed... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-printed",
    });

    for (const order of selected) {
      try {
        await updateOrderStatus(order.orderNumber, "Printed");
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-printed");
    setIsMarkingAsPrinted(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى Printed` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى Printed بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((o) => next.add(o.orderNumber));
      } else {
        visibleOrders.forEach((o) => next.delete(o.orderNumber));
      }
      return next;
    });
  };

  const applyFincartIdUpdates = (idUpdates) => {
    if (Object.keys(idUpdates).length === 0) return;
    setLoadedOrders((prev) =>
      prev.map((o) => {
        const update = idUpdates[o.orderNumber];
        if (!update) return o;
        const fincartOrderId =
          typeof update === "object" ? update.fincartOrderId : update;
        const alreadyInFincart =
          typeof update === "object" ? !!update.alreadyInFincart : false;
        return {
          ...o,
          fincartOrderId: fincartOrderId || o.fincartOrderId,
          sentToFincart: true,
          // اللي Fincart رجّعه duplicate / أو كان محفوظ قبل كده → orange old
          fincartSentThisSession: !alreadyInFincart,
          fincartDupSkipped: false,
        };
      })
    );
  };

  const markOrdersAsOldInFincart = (orders) => {
    if (!orders?.length) return;
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) =>
        nums.has(o.orderNumber)
          ? {
              ...o,
              sentToFincart: true,
              fincartSentThisSession: false,
              fincartDupSkipped: false,
            }
          : o
      )
    );
  };

  const markFincartDupSkipped = (orders) => {
    if (!orders?.length) return;
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) =>
        nums.has(o.orderNumber) ? { ...o, fincartDupSkipped: true } : o
      )
    );
  };

  const persistSentToFincart = async (orderIds, idUpdates = {}) => {
    if (!orderIds?.length) return;
    try {
      const items = Object.entries(idUpdates)
        .filter(([, update]) => update?.fincartOrderId)
        .map(([id, update]) => ({
          orderId: Number(id),
          shippingOrderId: update.fincartOrderId,
          trackingNumber: update.trackingNumber || null,
        }));
      await markOrdersSentToFincartBulk(orderIds, true, items);
    } catch (err) {
      console.error("فشل حفظ SentToFincart في الداتابيز:", err);
      toast.warning(
        "تم الإرسال لـ Fincart لكن فشل حفظ العلامة في السيرفر — حدّث يدوياً لو لزم"
      );
    }
  };

  const backfillSentToFincartFromLocalStorage = async (orders) => {
    const items = orders
      .map((o) => {
        const storedId = o.fincartOrderId || getStoredFincartOrderId(o.orderNumber);
        if (!storedId) return null;
        return {
          orderId: o.orderNumber,
          shippingOrderId: storedId,
        };
      })
      .filter(Boolean);
    if (items.length === 0) return;
    try {
      await markOrdersSentToFincartBulk(
        items.map((i) => i.orderId),
        true,
        items
      );
    } catch (err) {
      console.error("فشل مزامنة SentToFincart من localStorage:", err);
    }
  };

  const sendOrdersBatchToFincart = async (orders) => {
    let successCount = 0;
    let alreadyInFincartCount = 0;
    let failedCount = 0;
    const failedDetails = [];
    const alreadyInFincartDetails = [];
    const idUpdates = {};
    const phonesForWhatsApp = [];

    setIsSendingFincart(true);
    toast.info("جاري الإرسال إلى Fincart... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "fincart-send",
    });

    try {
      const [pickupLocationId, governorates] = await Promise.all([
        getDefaultPickupLocationId(),
        fetchFincartGovernorates(),
      ]);

      for (const order of orders) {
        try {
          const result = await sendOrderToFincart(
            order,
            pickupLocationId,
            governorates
          );
          const fincartOrderId = result?.fincartOrderId || result?.data?.orderId || null;
          const alreadyInFincart = !!result?.alreadyInFincart;

          if (fincartOrderId) {
            saveFincartOrderId(order.orderNumber, fincartOrderId);
            idUpdates[order.orderNumber] = {
              fincartOrderId,
              alreadyInFincart,
              trackingNumber: result?.trackingNumber || result?.orderCode || null,
            };
          } else {
            idUpdates[order.orderNumber] = {
              fincartOrderId: null,
              alreadyInFincart,
            };
          }

          if (order.phoneNumber) {
            phonesForWhatsApp.push(order.phoneNumber);
          }

          if (alreadyInFincart) {
            alreadyInFincartCount++;
            alreadyInFincartDetails.push(`#${order.orderNumber}`);
            console.log(
              `Fincart ALREADY/DUP #${order.orderNumber}:`,
              result?.data?.orderCode || result?.message,
              fincartOrderId
            );
          } else {
            successCount++;
            console.log(
              `Fincart OK #${order.orderNumber}:`,
              result?.data?.orderCode || result?.message,
              fincartOrderId
            );
          }
        } catch (err) {
          failedCount++;
          failedDetails.push(`#${order.orderNumber}: ${err.message}`);
          console.error(`Fincart failed #${order.orderNumber}:`, err);
        }
      }
    } catch (err) {
      toast.dismiss("fincart-send");
      setIsSendingFincart(false);
      throw err;
    }

    const sentIds = Object.keys(idUpdates).map((id) => Number(id) || id);
    await persistSentToFincart(sentIds, idUpdates);
    applyFincartIdUpdates(idUpdates);

    if (phonesForWhatsApp.length > 0) {
      try {
        await sendSentToCartWhatsApp(phonesForWhatsApp);
      } catch (waErr) {
        console.error("فشل إرسال تمبلت sent_to_cart:", waErr);
        toast.warning(
          "تم الإرسال لـ Fincart لكن فشل إرسال رسالة واتساب (sent_to_cart) للعميل"
        );
      }
    }

    toast.dismiss("fincart-send");
    setIsSendingFincart(false);

    return {
      successCount,
      alreadyInFincartCount,
      alreadyInFincartDetails,
      failedCount,
      failedDetails,
    };
  };

  const reportFincartSendResult = (
    successCount,
    failedCount,
    failedDetails,
    alreadyInFincartCount = 0,
    alreadyInFincartDetails = []
  ) => {
    if (successCount > 0) {
      toast.success(
        `تم إرسال ${successCount} طلب(ات) إلى Fincart بنجاح` +
          (failedCount > 0 ? `\nفشل ${failedCount}` : ""),
        { autoClose: 7000 }
      );
    }

    if (alreadyInFincartCount > 0) {
      toast.warning(
        `${alreadyInFincartCount} طلب كان موجود في Fincart (عميل/أوردر مكرر):\n${alreadyInFincartDetails.slice(0, 8).join(", ")}` +
          (alreadyInFincartDetails.length > 8 ? " ..." : "") +
          `\nاتعلموا كـ old in fincart`,
        { autoClose: 10000 }
      );
    }

    if (failedCount > 0) {
      toast.error(
        `فشل إرسال ${failedCount} طلب(ات):\n${failedDetails.slice(0, 5).join("\n")}` +
          (failedDetails.length > 5 ? "\n..." : ""),
        { autoClose: 12000 }
      );
    }
  };

  const handleSendSelectedToFincart = async () => {
    const selected = filteredOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const alreadySent = selected.filter((o) => o.sentToFincart);
    const toSend = selected.filter((o) => !o.sentToFincart);

    // اللي محفوظين مسبقاً في المتصفح → علّمهم old in fincart (برتقالي)
    if (alreadySent.length > 0) {
      markOrdersAsOldInFincart(alreadySent);
    }

    if (toSend.length === 0) {
      toast.info("كل الأوردرات المحددة مُرسلة لـ Fincart بالفعل");
      return;
    }

    const missingArea = toSend.filter((o) => !o.city?.trim());
    if (missingArea.length > 0) {
      toast.error(
        `${missingArea.length} طلب بدون مدينة/منطقة من قائمة Fincart (مثال: #${missingArea[0].orderNumber})`
      );
      return;
    }

    toast.info("جاري فحص التكرار قبل الإرسال إلى Fincart...", {
      autoClose: false,
      toastId: "fincart-dup-check",
    });

    const cleanOrders = [];
    const duplicateOrders = [];

    for (const order of toSend) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );
        if (count > 0) {
          duplicateOrders.push({ order, count });
        } else {
          cleanOrders.push(order);
        }
      } catch (err) {
        console.error(`فشل فحص التكرار #${order.orderNumber}:`, err);
        toast.warning(
          `فشل فحص التكرار لـ #${order.orderNumber} — اعتبره متكرر للمراجعة`
        );
        duplicateOrders.push({ order, count: "?" });
      }
    }

    toast.dismiss("fincart-dup-check");

    // أوردر واحد: لو متكرر → موافقة على التكرار ثم موافقة على Fincart
    if (toSend.length === 1) {
      const only = toSend[0];
      const dup = duplicateOrders[0];

      if (dup) {
        const agreeDuplicate = window.confirm(
          `تحذير: هذا العميل لديه ${dup.count} طلب(ات) سابقة بنفس الرقم!\n\nالطلب #${only.orderNumber} — ${only.name}\nالرقم: ${only.phoneNumber}\n\nهل توافق على المتابعة؟`
        );
        if (!agreeDuplicate) {
          markFincartDupSkipped([only]);
          toast.info("تم إلغاء الإرسال بسبب تكرار الرقم");
          return;
        }
      }

      const confirmSend = window.confirm(
        `هل توافق على إرسال الطلب #${only.orderNumber} إلى Fincart للشحن؟` +
          (alreadySent.length > 0
            ? `\n\n(سيتم تخطي ${alreadySent.length} مُرسلين مسبقاً من التحديد)`
            : "") +
          `\n\nلن يتم تغيير حالة الطلب — حدّث يدوياً بعدين.`
      );
      if (!confirmSend) return;

      try {
        const {
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails,
        } = await sendOrdersBatchToFincart([only]);
        reportFincartSendResult(
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails
        );
      } catch (err) {
        toast.error(err.message || "فشل التحضير للإرسال إلى Fincart");
      }
      return;
    }

    // أكثر من أوردر: ابعت غير المتكرر أولاً، ثم اسأل عن المتكرر
    let totalSuccess = 0;
    let totalFailed = 0;
    let totalAlreadyInFincart = 0;
    const allFailedDetails = [];
    const allAlreadyInFincartDetails = [];

    if (cleanOrders.length > 0) {
      const confirmClean = window.confirm(
        `إرسال ${cleanOrders.length} طلب(ات) إلى Fincart للشحن؟` +
          (duplicateOrders.length > 0
            ? `\n\nسيتم تخطي ${duplicateOrders.length} طلب(ات) متكررة مؤقتاً — هسألك عنهم بعد الإرسال`
            : "") +
          (alreadySent.length > 0
            ? `\n\n(سيتم تخطي ${alreadySent.length} مُرسلين مسبقاً)`
            : "") +
          `\n\nلن يتم تغيير حالة الطلب — حدّث يدوياً بعدين.`
      );
      if (!confirmClean) {
        toast.info("تم إلغاء الإرسال");
        return;
      }

      try {
        const {
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails,
        } = await sendOrdersBatchToFincart(cleanOrders);
        totalSuccess += successCount;
        totalFailed += failedCount;
        totalAlreadyInFincart += alreadyInFincartCount;
        allFailedDetails.push(...failedDetails);
        allAlreadyInFincartDetails.push(...alreadyInFincartDetails);
      } catch (err) {
        toast.error(err.message || "فشل التحضير للإرسال إلى Fincart");
        return;
      }
    }

    if (duplicateOrders.length > 0) {
      const dupList = duplicateOrders
        .slice(0, 15)
        .map(
          ({ order, count }) =>
            `#${order.orderNumber} — ${order.name} (${count} سابق)`
        )
        .join("\n");

      const sendDuplicates = window.confirm(
        `فيه ${duplicateOrders.length} طلب(ات) متكررة مراحتش لـ Fincart.\n\n${dupList}` +
          (duplicateOrders.length > 15 ? "\n..." : "") +
          `\n\nهل عايز تبعتها برضو؟`
      );

      if (sendDuplicates) {
        try {
          const {
            successCount,
            failedCount,
            failedDetails,
            alreadyInFincartCount,
            alreadyInFincartDetails,
          } = await sendOrdersBatchToFincart(
            duplicateOrders.map(({ order }) => order)
          );
          totalSuccess += successCount;
          totalFailed += failedCount;
          totalAlreadyInFincart += alreadyInFincartCount;
          allFailedDetails.push(...failedDetails);
          allAlreadyInFincartDetails.push(...alreadyInFincartDetails);
          // اللي فشلوا من المتكرر يفضلوا ظاهرين كـ DUP عشان تتعرف عليهم
          if (failedCount > 0) {
            const failedNums = new Set(
              failedDetails
                .map((d) => {
                  const m = d.match(/^#([^:]+):/);
                  return m ? String(m[1]).trim() : null;
                })
                .filter(Boolean)
            );
            markFincartDupSkipped(
              duplicateOrders
                .map(({ order }) => order)
                .filter((o) => failedNums.has(String(o.orderNumber)))
            );
          }
        } catch (err) {
          markFincartDupSkipped(duplicateOrders.map(({ order }) => order));
          toast.error(err.message || "فشل التحضير لإرسال المتكرر إلى Fincart");
        }
      } else {
        markFincartDupSkipped(duplicateOrders.map(({ order }) => order));
        toast.info(`تم تخطي ${duplicateOrders.length} طلب(ات) متكررة`);
      }
    }

    reportFincartSendResult(
      totalSuccess,
      totalFailed,
      allFailedDetails,
      totalAlreadyInFincart,
      allAlreadyInFincartDetails
    );
  };

  const handlePrintSelectedLabels = async () => {
    const selected = filteredOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const withFincartId = selected.filter((o) => o.fincartOrderId);
    const withoutFincartId = selected.filter((o) => !o.fincartOrderId);

    if (withFincartId.length === 0) {
      toast.error("الأوردرات المحددة مش مُرسلة لـ Fincart بعد — ابعتها الأول");
      return;
    }

    if (withoutFincartId.length > 0) {
      const ok = window.confirm(
        `${withoutFincartId.length} طلب مش مُرسل لـ Fincart وهيتخطى.\n\nطباعة بوليصة لـ ${withFincartId.length} طلب؟`
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        `طباعة بوليصة شحن حرارية لـ ${withFincartId.length} طلب على Xprinter XP-420B؟`
      );
      if (!ok) return;
    }

    setIsPrintingLabels(true);
    toast.info("جاري جلب وطباعة البوليصات... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "fincart-print",
    });

    try {
      const fincartIds = withFincartId.map((o) => o.fincartOrderId);
      const pdfBlob = await fetchFincartThermalLabelsPdf(fincartIds);
      const base64 = await blobToBase64(pdfBlob);
      await printPdfBase64(base64);
      toast.dismiss("fincart-print");
      toast.success(`تم إرسال ${withFincartId.length} بوليصة للطابعة`);
    } catch (err) {
      console.error("Fincart print failed:", err);
      toast.dismiss("fincart-print");
      toast.error(err.message || "فشل طباعة البوليصات");
    } finally {
      setIsPrintingLabels(false);
    }
  };

  const handlePrintVisibleOrderPieces = async () => {
    const visible = loadedOrders.filter((order) => {
      if (fincartFilter === "sent" && !order.sentToFincart) return false;
      if (fincartFilter === "notSent" && (order.sentToFincart || order.fincartDupSkipped))
        return false;
      if (fincartFilter === "dup" && !order.fincartDupSkipped) return false;
      if (governorateFilter !== "all" && order.state !== governorateFilter) return false;

      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        order.orderNumber.toString().includes(term) ||
        order.name.toLowerCase().includes(term) ||
        order.phoneNumber.includes(term) ||
        order.phoneNumber2?.includes(term)
      );
    });

    if (visible.length === 0) {
      toast.warning("لا توجد طلبات ظاهرة للطباعة");
      return;
    }

    const labels = visible.map((o) => ({
      orderNumber: o.orderNumber,
      pieces: o.items?.length || 0,
    }));

    if (
      !window.confirm(
        `طباعة ملصقات رقم الأوردر + عدد القطع؟\n\n${labels.length} ملصق على ورق 50×25`
      )
    ) {
      return;
    }

    setIsPrintingOrderPieces(true);
    try {
      const count = await printOrderPieceLabels(labels);
      toast.success(`تم طباعة ${count} ملصق`);
    } catch (err) {
      console.error("Order piece print failed:", err);
      toast.error(err.message || "فشل طباعة الملصقات");
    } finally {
      setIsPrintingOrderPieces(false);
    }
  };

  const filteredOrders = loadedOrders.filter((order) => {
    if (fincartFilter === "sent" && !order.sentToFincart) return false;
    if (fincartFilter === "notSent" && (order.sentToFincart || order.fincartDupSkipped))
      return false;
    if (fincartFilter === "dup" && !order.fincartDupSkipped) return false;
    if (governorateFilter !== "all" && order.state !== governorateFilter) return false;

    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  const fincartFilterCounts = {
    all: loadedOrders.length,
    sent: loadedOrders.filter((o) => o.sentToFincart).length,
    notSent: loadedOrders.filter((o) => !o.sentToFincart && !o.fincartDupSkipped)
      .length,
    dup: loadedOrders.filter((o) => o.fincartDupSkipped).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Package className="text-indigo-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Girabatcom
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {(error || queryError) && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6 border border-red-200">
            {error || "فشل في تحميل بيانات طلبات Girabatcom. يرجى المحاولة مرة أخرى."}
          </div>
        )}

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-1 bg-indigo-100 rounded-xl">
                  <Package className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص طلبات Girabatcom</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-indigo-600">{girabatcomOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded-xl">
                    <span className="text-gray-600">عدد القطع</span>
                    <span className="text-2xl font-bold text-slate-700">{girabatcomOrdersSummary.totalProductsSold ?? 0} قطعة</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{girabatcomOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي البيع (بدون الشحن)</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                    <span className="text-gray-600">الإجمالي</span>
                    <span className="text-2xl font-bold text-purple-600">{girabatcomOrdersSummary.totalProfit} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div> */}

        {/* Bulk Actions */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-green-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <CopyCheck className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</h3>
                  <p className="text-sm text-green-700">نسخ {loadedOrders.length} طلب مع فحص التكرار</p>
                </div>
              </div>
              <button
                onClick={handleCopyAllVisibleOrders}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={20} />
                نسخ الكل
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-purple-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Archive className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-purple-900 text-xl">تحويل المحدد إلى قديم</h3>
                  <p className="text-sm text-purple-700">
                    بناءً على الـ checkbox — المحدد: {selectedOrderNumbers.size}
                  </p>
                </div>
              </div>
              <button
                onClick={handleMarkSelectedAsOld}
                disabled={isOrdersLoading || selectedOrderNumbers.size === 0 || isSendingFincart || isPrintingLabels}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Archive size={20} />
                تحويل المحدد
              </button>
            </div>
          </div>
        </div> */}

        {/* Fincart Send + Print */}
        {/* <div className="bg-gradient-to-r from-cyan-50 to-sky-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border-2 border-cyan-300">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-100 rounded-xl">
                  <Truck className="text-cyan-700" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-cyan-900 text-lg">Fincart — شحن وطباعة</h3>
                  <p className="text-sm text-cyan-700">
                    المحدد: <span className="font-bold">{selectedOrderNumbers.size}</span> طلب
                    {" · "}الإرسال بدون تغيير الحالة · الطباعة حرارية على XP-420B
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(true, filteredOrders)}
                  disabled={filteredOrders.length === 0 || isSendingFincart || isPrintingLabels}
                  className="bg-white border-2 border-cyan-400 text-cyan-800 font-bold px-5 py-3 rounded-xl hover:bg-cyan-50 disabled:opacity-50 transition-all flex items-center gap-2"
                >
                  <CheckSquare size={18} />
                  تحديد الكل
                </button>
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(false, filteredOrders)}
                  disabled={selectedOrderNumbers.size === 0 || isSendingFincart || isPrintingLabels}
                  className="bg-white border-2 border-gray-300 text-gray-700 font-medium px-5 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all"
                >
                  إلغاء التحديد
                </button>
              </div>
              <button
                type="button"
                onClick={handleSendSelectedToFincart}
                disabled={selectedOrderNumbers.size === 0 || isSendingFincart || isPrintingLabels}
                className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isSendingFincart ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    جاري الإرسال...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    إرسال المحدد لـ Fincart
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handlePrintSelectedLabels}
                disabled={selectedOrderNumbers.size === 0 || isSendingFincart || isPrintingLabels}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-bold px-4 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isPrintingLabels ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    جاري الطباعة...
                  </>
                ) : (
                  <>
                    <Printer size={20} />
                    طباعة بوليصة المحدد
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleMarkSelectedAsOld}
                disabled={
                  isOrdersLoading ||
                  selectedOrderNumbers.size === 0 ||
                  isSendingFincart ||
                  isPrintingLabels
                }
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-4 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Archive size={20} />
                تحويل للقديم ({selectedOrderNumbers.size})
              </button>
            </div>
          </div>
        </div> */}

        {/* Reference Section */}
        {/* <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-indigo-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">الرقم الحالي</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num + 1;
                      const newRef = currentReference.replace(/\d+$/, newNum.toString());
                      setCurrentReference(newRef);
                      setReferenceInput(newRef);
                      localStorage.setItem("orderReference", newRef);
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-r-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="زيادة الرقم"
                >
                  <Plus size={20} />
                </button>
                <div className="flex-1 text-3xl font-bold text-indigo-600 bg-indigo-50 py-3 px-4 text-center border-2 border-indigo-200">
                  {currentReference}
                </div>
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num - 1;
                      if (newNum >= 0) {
                        const newRef = currentReference.replace(/\d+$/, newNum.toString());
                        setCurrentReference(newRef);
                        setReferenceInput(newRef);
                        localStorage.setItem("orderReference", newRef);
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-l-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="إنقاص الرقم"
                >
                  <Minus size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
                placeholder="Girabatcom.4000"
                style={{ fontFamily: "cairo black !important" }}
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل ({loadedOrders.length})
            </button>
            <button
              onClick={handleMarkSelectedAsOld}
              disabled={isOrdersLoading || selectedOrderNumbers.size === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Archive size={20} />
              تحويل المحدد للقديم ({selectedOrderNumbers.size})
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            كل ما تنسخ بيانات عميل بعد "تحديث إلى قديم"، الرقم سيزيد تلقائيًا
          </p>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                showFilters || fincartFilter !== "all" || governorateFilter !== "all"
                  ? "bg-cyan-50 border-cyan-400 text-cyan-800"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {(fincartFilter !== "all" || governorateFilter !== "all") && (
                <span className="text-xs font-bold bg-cyan-600 text-white rounded-full px-2 py-0.5">
                  {(fincartFilter !== "all" ? 1 : 0) + (governorateFilter !== "all" ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">المحافظة:</span>
                <select
                  value={governorateFilter}
                  onChange={(e) => setGovernorateFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white text-sm font-medium min-w-[200px]"
                >
                  <option value="all">كل المحافظات</option>
                  {EGYPTIAN_GOVERNORATES.map((gov) => (
                    <option key={gov} value={gov}>
                      {gov}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {[
                  { key: "all", label: "الكل", count: fincartFilterCounts.all, active: "bg-indigo-600 text-white border-indigo-600" },
                  { key: "notSent", label: "مراحش Fincart", count: fincartFilterCounts.notSent, active: "bg-gray-700 text-white border-gray-700" },
                  { key: "sent", label: "راح Fincart", count: fincartFilterCounts.sent, active: "bg-orange-500 text-white border-orange-500" },
                  { key: "dup", label: "DUP", count: fincartFilterCounts.dup, active: "bg-red-600 text-white border-red-600" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFincartFilter(opt.key)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                      fincartFilter === opt.key
                        ? opt.active
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label} ({opt.count})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Page Size Selector */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
                              <span className="text-2xl text-gray-600">
                (المعروض: {filteredOrders.length} / المحمّل: {loadedOrders.length})
              </span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-indigo-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
                          <button
                type="button"
                onClick={handleMarkSelectedAsPrinted}
                disabled={
                  isOrdersLoading ||
                  selectedOrderNumbers.size === 0 ||
                  isSendingFincart ||
                  isPrintingLabels ||
                  isPrintingOrderPieces ||
                  isMarkingAsPrinted ||
                  isMarkingAsOld
                }
                className="bg-teal-600 text-2xl hover:bg-teal-700 disabled:bg-gray-400 text-white font-bold px-5 py-5 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                title="تحويل المحدد إلى Printed"
              >
                {isMarkingAsPrinted ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    جاري التحويل...
                  </>
                ) : (
                  <>
                    <Printer size={20} />
                    تحويل لـ Printed ({selectedOrderNumbers.size})
                  </>
                )}
              </button>
            <button
                type="button"
                onClick={handleSendSelectedToFincart}
                disabled={selectedOrderNumbers.size === 0 || isSendingFincart || isPrintingLabels || isPrintingOrderPieces || isMarkingAsPrinted || isMarkingAsOld}
                className="bg-cyan-600 text-lg hover:bg-cyan-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSendingFincart ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    جاري الإرسال...
                  </>
                ) : (
              <>
                <Send size={20} />
                إرسال المحدد لـ Fincart
                </>
                )}
                </button>

              <button
                type="button"
                onClick={handlePrintVisibleOrderPieces}
                disabled={
                  isOrdersLoading ||
                  filteredOrders.length === 0 ||
                  isSendingFincart ||
                  isPrintingLabels ||
                  isPrintingOrderPieces ||
                  isMarkingAsPrinted ||
                  isMarkingAsOld
                }
                className="bg-emerald-600 text-sm hover:bg-emerald-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                title="طباعة رقم الأوردر وعدد القطع على ورق 50×25"
              >
                {isPrintingOrderPieces ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    جاري الطباعة...
                  </>
                ) : (
                  <>
                    <Printer size={16} />
                    طباعة أرقام ({filteredOrders.length})
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleMarkSelectedAsOld}
                disabled={
                  isOrdersLoading ||
                  selectedOrderNumbers.size === 0 ||
                  isSendingFincart ||
                  isPrintingLabels ||
                  isPrintingOrderPieces ||
                  isMarkingAsPrinted ||
                  isMarkingAsOld
                }
                className="bg-purple-600 text-sm hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-6 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isMarkingAsOld ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    جاري التحويل...
                  </>
                ) : (
                  <>
                    <Archive size={16} />
                    تحويل للقديم ({selectedOrderNumbers.size})
                  </>
                )}
              </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1000px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                        <th className="p-2 text-center font-bold text-indigo-800">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-cyan-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) =>
                                  selectedOrderNumbers.has(o.orderNumber)
                                )
                              }
                              onChange={(e) =>
                                setSelectAllVisible(e.target.checked, filteredOrders)
                              }
                            />
                            <span className="text-xs">الطلب</span>
                          </div>
                        </th>
                        <th className="p-2 text-right font-bold text-indigo-800">بيانات العميل</th>
                        <th className="p-4 text-right font-bold text-indigo-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-indigo-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-indigo-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-indigo-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-cyan-600"
                                checked={selectedOrderNumbers.has(order.orderNumber)}
                                onChange={() => toggleSelectOrder(order.orderNumber)}
                              />
                              <span className="font-mono font-bold text-indigo-600 text-sm">#{order.orderNumber}</span>
                              {order.sentToFincart ? (
                                <span
                                  title={
                                    order.fincartSentThisSession
                                      ? "تم الإرسال لـ Fincart في هذه الجلسة"
                                      : "مُرسل لـ Fincart مسبقاً"
                                  }
                                  className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${
                                    order.fincartSentThisSession
                                      ? "text-cyan-700 bg-cyan-50 border-cyan-200"
                                      : "text-orange-700 bg-orange-50 border-orange-200"
                                  }`}
                                >
                                  {order.fincartSentThisSession ? "Fincart ✓" : "old in fincart"}
                                </span>
                              ) : order.fincartDupSkipped ? (
                                <span
                                  title="تم تخطيه بسبب تكرار الرقم"
                                  className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                >
                                  DUP
                                </span>
                              ) : null}
                              {(order.fincartOrderId || order.sentToFincart) && (
                                <ShippingTrackButton orderId={order.orderNumber} />
                              )}
                            </div>
                          </td>

                          <td className="p-2 max-w-[180px]">
                            <div className="space-y-0.5 text-[20px] leading-tight">
                              <div className="flex items-center gap-1">
                                <User size={11} className="text-gray-500 flex-shrink-0" />
                                <span style={{ fontFamily: "cairo black" }} className="font-semibold text-gray-800 truncate">
                                  {order.name}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  copyToClipboard(order.phoneNumber);
                                  toast.success("تم نسخ رقم الموبايل");
                                }}
                                title="نسخ الرقم"
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition cursor-pointer"
                              >
                                <Phone size={10} className="flex-shrink-0" />
                                <span className="truncate">{order.phoneNumber}</span>
                              </button>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && order.phoneNumber2 !== "Unknown Phone" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    copyToClipboard(order.phoneNumber2);
                                    toast.success("تم نسخ رقم الموبايل");
                                  }}
                                  title="نسخ الرقم"
                                  className="flex items-center gap-1 text-green-600 hover:text-green-800 transition cursor-pointer"
                                >
                                  <Phone size={10} className="flex-shrink-0" />
                                  <span className="truncate">{order.phoneNumber2}</span>
                                </button>
                              )}
                              <div className="flex items-start gap-1">
                                <MapPin size={11} className="text-gray-500 mt-0.5 flex-shrink-0" />
                                <span style={{ fontFamily: "cairo black" }} className="text-gray-700 line-clamp-2">
                                  {order.address}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Map size={11} className="text-gray-500 flex-shrink-0" />
                                <span className="text-purple-600 font-medium truncate">{order.state}</span>
                              </div>
                              {order.city ? (
                                <p className="text-cyan-700 truncate">المنطقة: {order.city}</p>
                              ) : (
                                <p className="text-red-500">بدون منطقة — لن يُرسل</p>
                              )}
                              <p className="font-bold text-green-600 text-2xl pt-0.5">
                                {order.total} ج.م
                              </p>
                            </div>
                          </td>

                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-indigo-700">{item.name}</div>
                                    <div style={{ fontFamily: "cairo black" }} className="text-[13px] text-black-600">
                                      {item.type}
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {item.hasPersonalizedName ? <PersonalizedBadge /> : null}
                                      <CaseStyleBadge name={item.caseStyleName} />
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="text-green-600 hover:text-green-800 transition-transform hover:scale-110"
                                              title="تحميل الصورة"
                                            >
                                              <Download size={16} />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {index < order.items.length - 1 && <hr className="my-2 border-gray-200" />}
                              </div>
                            ))}
                          </td>

                          <td className="p-2 max-w-[150px]">
                            <p style={{ fontFamily: "cairo black" }} className="text-lg text-gray-600 line-clamp-5">
                              {order.note || "—"}
                            </p>
                          </td>

                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Edit size={14} />
                                  تغييرات
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                                className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <DollarSign size={14} />
                                  ديبوزت
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                                className="bg-slate-700 text-white px-3 py-2 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printing Hold
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printed")}
                                className="bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printed
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Incomplete")}
                                className="bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Incomplete
                                </div>
                              </button>
                              {isAdmin(authState.role) && (
                                <button
                                  onClick={() => handleCopyAllExcelData(order)}
                                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                                >
                                <Copy size={16} />
                                نسخ (TeePlus)
                                </button>
                              )}
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Package className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات Girabatcom</p>
                  <p className="text-lg text-gray-500">سيتم عرض طلبات Girabatcom هنا</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="العودة إلى الأعلى"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GirabatcomOrdersPage;
